<template>
  <section id="products" class="sec_home-products">
    <PartsHomeTitle
      title="人気の商品ランキング"
      subtitle="人気商品からぴったりなアイテムを見つけてみよう"
    />

    <div class="products-tabs" role="tablist" aria-label="商品カテゴリ">
      <button
        v-for="tab in PRODUCT_TABS"
        :id="`products-tab-${tab.id}`"
        :key="tab.id"
        type="button"
        role="tab"
        class="products-tab-btn"
        :class="{ 'is-active': tab.id === activeTabId }"
        :aria-selected="tab.id === activeTabId"
        aria-controls="products-panel"
        @click="selectTab(tab)"
      >
        <div v-if="tab.iconName" class="products-tab_icon" :class="tab.iconModifier">
          <img class="icon-on" :src="iconUrl(tab.iconName, 'wh')" alt="" />
          <img class="icon-off" :src="iconUrl(tab.iconName, 'green')" alt="" />
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -- 定数リテラルの `<br class="sp-only">` のみ。外部入力は通さない -->
        <span v-html="tab.label"></span>
      </button>
    </div>

    <CommonsProductGrid
      id="products-panel"
      role="tabpanel"
      :aria-labelledby="`products-tab-${activeTabId}`"
      :product-contents="visibleProductContents"
    />
  </section>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `sec_home-products`（人気の商品ランキング）。
 *
 * タブは5本（→ `ProductTabConst`）。「すべて」は `is_top` の一覧、残る4本は全商品からカテゴリで絞る（決定#31）。
 * 支給JSは全カードをDOMに置いて `is-hidden` で隠すが、ここは `computed` で配列を絞るのでDOMを作らない。
 *
 * `id="products"` はハンバーガーメニューとFVカルーセルの遷移先アンカー（決定#26・#30）。
 * ハンバーガーのカテゴリは `?category=<カテゴリ英名>` を付けて飛んでくるので、選択タブは URL で持つ。
 * `role="tablist"` / `aria-selected` は支給HTMLに無いが §3.6-3 で足すと決めている。
 */
import type { ProductContent } from '@/types/domains/ProductContentType';
import type { ProductTab } from '@/types/domains/ProductTabType';
import { PRODUCT_TABS } from '@/consts/ProductTabConst';
import { PRODUCT_CONTENT_CONSTANTS } from '@/consts/ProductContentConst';
import { PRODUCTS_ANCHOR_HASH, PRODUCTS_CATEGORY_QUERY } from '@/consts/ChromeConst';
import { KENCOS_NOTICE_CONSTANTS } from '@/consts/KencosNoticeConst';
import { TopPresenter } from '@/presenters/TopPresenter';

const presenter = new TopPresenter();
const route = useRoute();
const router = useRouter();

/** タブの `categoryId` に対応するカテゴリ英名。`?category=` の値になる。「すべて」は持たない */
const categoryNameOf = (tab: ProductTab): string | undefined =>
  typeof tab.categoryId === 'number'
    ? PRODUCT_CONTENT_CONSTANTS.CATEGORY_ENGLISH_NAME[String(tab.categoryId)]
    : undefined;

/**
 * 選択中のタブは URL の `?category=` が正。ローカルの ref に持たない。
 *
 * ハンバーガーのカテゴリはこのクエリを付けて飛んでくるので、
 * トップに居るまま押されても（コンポーネントが再生成されなくても）タブが切り替わる。
 * タブを押したときも `selectTab` が URL を書き換えるため、同じカテゴリを続けて押しても取りこぼさない。
 * 指定が無い／未知の値なら先頭の「すべて」。
 */
const activeTabId = computed<string>(() => {
  const name = route.query[PRODUCTS_CATEGORY_QUERY];
  return PRODUCT_TABS.find((tab) => categoryNameOf(tab) === name)?.id ?? PRODUCT_TABS[0].id;
});

/** 履歴を積まずに URL だけ差し替える。ハッシュを付け直すので「人気の商品ランキング」の頭に戻る */
const selectTab = (tab: ProductTab): void => {
  router.replace({
    query: { ...route.query, [PRODUCTS_CATEGORY_QUERY]: categoryNameOf(tab) },
    hash: PRODUCTS_ANCHOR_HASH,
  });
};

/**
 * パーソナルケアのタブが表示されたらケンコスの告知を出す（`KencosNoticeConst`）。
 * クリックではなく表示中のタブを合図にするのは、ハンバーガー・パンくず・旧URL（`?category=` 付きで来る）も拾うため。
 * SSR では出さない（ダイアログの `resolve` はサーバから引き継げない）ので、`onMounted` で見張り始める。
 */
const { show } = useDialog();
onMounted(() => {
  watch(
    activeTabId,
    (tabId) => {
      if (KENCOS_NOTICE_CONSTANTS.ENABLED && tabId === 'personalcare') {
        show(KENCOS_NOTICE_CONSTANTS.MESSAGE, KENCOS_NOTICE_CONSTANTS.TITLE);
      }
    },
    { immediate: true }
  );
});

/** カテゴリ4本の母集団。商品コンテンツは `plugins/201-fetch-product-content.ts` が起動時に取得済み */
const allProductContents = computed<ProductContent[]>(() =>
  presenter.getProductContentsByCategory('all')
);

/**
 * 「すべて」タブの母集団。obbs_api が `is_top` で絞った一覧で、現行トップの掲載商品・並び順と同じ。
 * `all` とは取得URLもサーバ側のソートも別（`/productContents/top`）なので、ここで再ソートしない。
 */
const topProductContents = computed<ProductContent[]>(() =>
  presenter.getProductContentsByCategory('top')
);

const visibleProductContents = computed<ProductContent[]>(() => {
  const tab = PRODUCT_TABS.find(({ id }) => id === activeTabId.value);
  if (!tab || tab.categoryId === null) return [];
  if (tab.categoryId === 'top') return topProductContents.value;

  // カテゴリIDは `resolveCategoryId` 経由で引く。APIの生値は旧3分類のままで食品（4）が出ないため
  return allProductContents.value.filter(
    (productContent) => presenter.resolveCategoryId(productContent) === tab.categoryId
  );
});

const iconUrl = (iconName: string, state: 'wh' | 'green'): string =>
  `/image/products-icon_${iconName}_${state}.png`;
</script>
